/**
 * The canonical permission catalog for LaundryPro.
 *
 * This file is the SINGLE SOURCE OF TRUTH for every permission in the system.
 * Three consumers derive from it and none of them may define permissions of
 * their own:
 *
 *   1. The database — `migrations/0002_seed_rbac.sql` is GENERATED from this
 *      file by `scripts/generate-rbac-seed.mjs`. A unit test regenerates it and
 *      fails if the committed SQL has drifted from this catalog.
 *   2. The Worker — `requirePermission("...")` accepts only a `PermissionKey`,
 *      so a typo is a compile error rather than a silently open endpoint.
 *   3. The web app — navigation is built from `NAV_MODULES` below, so a module
 *      is shown only when the user holds its view permission.
 *
 * Consumer (3) is a user-experience affordance ONLY. Hiding a nav item is never
 * authorization; the Worker independently enforces every permission on every
 * request.
 */

export type PermissionAction =
  | "view"
  | "create"
  | "update"
  | "delete"
  | "cancel"
  | "void"
  | "print"
  | "export_pdf"
  | "issue"
  | "download"
  | "regenerate_pdf"
  | "export"
  | "refund"
  | "disable";

export interface PermissionDefinition {
  /** Stable identifier, `module.action`. Never renamed once released. */
  readonly key: string;
  readonly module: string;
  readonly action: PermissionAction;
  readonly labelEn: string;
  readonly labelAr: string;
  /**
   * Marks a permission that changes business identity, tax configuration,
   * authorization itself, or destroys financial history. Sensitive permissions
   * are never included in a non-owner role by the default seed; an owner may
   * still grant them deliberately to a custom role.
   */
  readonly sensitive?: true;
}

/**
 * The catalog. Ordering here determines `permissions.sort_order` in the
 * database, which in turn drives the display order in the roles editor.
 */
export const PERMISSIONS = [
  // ── Dashboard ────────────────────────────────────────────────────────────
  { key: "dashboard.view", module: "dashboard", action: "view", labelEn: "View dashboard", labelAr: "عرض لوحة التحكم" },

  // ── Orders ───────────────────────────────────────────────────────────────
  { key: "orders.view", module: "orders", action: "view", labelEn: "View orders", labelAr: "عرض الطلبات" },
  { key: "orders.create", module: "orders", action: "create", labelEn: "Create orders", labelAr: "إنشاء الطلبات" },
  { key: "orders.update", module: "orders", action: "update", labelEn: "Update orders", labelAr: "تعديل الطلبات" },
  { key: "orders.cancel", module: "orders", action: "cancel", labelEn: "Cancel orders", labelAr: "إلغاء الطلبات" },

  // ── Customers ────────────────────────────────────────────────────────────
  { key: "customers.view", module: "customers", action: "view", labelEn: "View customers", labelAr: "عرض العملاء" },
  { key: "customers.create", module: "customers", action: "create", labelEn: "Create customers", labelAr: "إضافة العملاء" },
  { key: "customers.update", module: "customers", action: "update", labelEn: "Update customers", labelAr: "تعديل العملاء" },
  { key: "customers.delete", module: "customers", action: "delete", labelEn: "Delete customers", labelAr: "حذف العملاء" },

  // ── Services ─────────────────────────────────────────────────────────────
  { key: "services.view", module: "services", action: "view", labelEn: "View services", labelAr: "عرض الخدمات" },
  { key: "services.create", module: "services", action: "create", labelEn: "Create services", labelAr: "إضافة الخدمات" },
  { key: "services.update", module: "services", action: "update", labelEn: "Update services", labelAr: "تعديل الخدمات" },
  { key: "services.delete", module: "services", action: "delete", labelEn: "Delete services", labelAr: "حذف الخدمات" },

  // ── Pricing ──────────────────────────────────────────────────────────────
  { key: "pricing.view", module: "pricing", action: "view", labelEn: "View pricing", labelAr: "عرض الأسعار" },
  { key: "pricing.create", module: "pricing", action: "create", labelEn: "Create prices", labelAr: "إضافة الأسعار" },
  { key: "pricing.update", module: "pricing", action: "update", labelEn: "Update prices", labelAr: "تعديل الأسعار", sensitive: true },
  { key: "pricing.delete", module: "pricing", action: "delete", labelEn: "Delete prices", labelAr: "حذف الأسعار", sensitive: true },

  // ── Invoices ─────────────────────────────────────────────────────────────
  { key: "invoices.view", module: "invoices", action: "view", labelEn: "View invoices", labelAr: "عرض الفواتير" },
  { key: "invoices.create", module: "invoices", action: "create", labelEn: "Create invoices", labelAr: "إصدار الفواتير" },
  { key: "invoices.update", module: "invoices", action: "update", labelEn: "Edit invoices", labelAr: "تعديل الفواتير", sensitive: true },
  { key: "invoices.void", module: "invoices", action: "void", labelEn: "Void invoices", labelAr: "إبطال الفواتير", sensitive: true },
  { key: "invoices.delete", module: "invoices", action: "delete", labelEn: "Delete invoices", labelAr: "حذف الفواتير", sensitive: true },
  { key: "invoices.print", module: "invoices", action: "print", labelEn: "Print invoices", labelAr: "طباعة الفواتير" },
  { key: "invoices.export_pdf", module: "invoices", action: "export_pdf", labelEn: "Export invoices as PDF", labelAr: "تصدير الفواتير PDF" },
  // Separate from `invoices.create`: drafting a document and turning it into a
  // legal tax record with a permanent number are different levels of authority.
  // NOT `sensitive` — that flag means changing business identity, tax config,
  // authorization, or DESTROYING financial history. Issuing creates history
  // forward, like orders.create, and the counter staff who hand the customer
  // their invoice must hold it.
  { key: "invoices.issue", module: "invoices", action: "issue", labelEn: "Issue invoices", labelAr: "إصدار الفواتير" },
  // Separate from `invoices.print`: a downloaded file leaves the building.
  { key: "invoices.download", module: "invoices", action: "download", labelEn: "Download invoices", labelAr: "تنزيل الفواتير" },
  // SENSITIVE, deliberately: regenerating an archived PDF is the one action
  // in this module that creates a new version of a stored financial document.
  // An ordinary download always serves the existing archived copy — this
  // permission gates only the explicit "make a new one" action, and by the
  // sensitive-permission rule below it is never in a non-owner role's default
  // grants.
  { key: "invoices.regenerate_pdf", module: "invoices", action: "regenerate_pdf", labelEn: "Regenerate invoice PDFs", labelAr: "إعادة توليد ملفات PDF للفواتير", sensitive: true },

  // ── Payments ─────────────────────────────────────────────────────────────
  { key: "payments.view", module: "payments", action: "view", labelEn: "View payments", labelAr: "عرض المدفوعات" },
  { key: "payments.create", module: "payments", action: "create", labelEn: "Record payments", labelAr: "تسجيل المدفوعات" },
  { key: "payments.refund", module: "payments", action: "refund", labelEn: "Refund payments", labelAr: "استرجاع المدفوعات", sensitive: true },
  // Amending a recorded payment rewrites the money ledger, so it is sensitive
  // even though "update" sounds routine elsewhere in the catalogue.
  { key: "payments.update", module: "payments", action: "update", labelEn: "Amend payments", labelAr: "تعديل المدفوعات", sensitive: true },

  // ── Expenses ─────────────────────────────────────────────────────────────
  { key: "expenses.view", module: "expenses", action: "view", labelEn: "View expenses", labelAr: "عرض المصروفات" },
  { key: "expenses.create", module: "expenses", action: "create", labelEn: "Create expenses", labelAr: "إضافة المصروفات" },
  { key: "expenses.update", module: "expenses", action: "update", labelEn: "Update expenses", labelAr: "تعديل المصروفات" },
  { key: "expenses.delete", module: "expenses", action: "delete", labelEn: "Delete expenses", labelAr: "حذف المصروفات" },

  // ── Reports ──────────────────────────────────────────────────────────────
  { key: "reports.view", module: "reports", action: "view", labelEn: "View reports", labelAr: "عرض التقارير" },
  { key: "reports.export", module: "reports", action: "export", labelEn: "Export reports", labelAr: "تصدير التقارير" },

  // ── Inventory ────────────────────────────────────────────────────────────
  { key: "inventory.view", module: "inventory", action: "view", labelEn: "View inventory", labelAr: "عرض المخزون" },
  { key: "inventory.create", module: "inventory", action: "create", labelEn: "Create inventory items", labelAr: "إضافة أصناف المخزون" },
  { key: "inventory.update", module: "inventory", action: "update", labelEn: "Update inventory", labelAr: "تعديل المخزون" },
  { key: "inventory.delete", module: "inventory", action: "delete", labelEn: "Delete inventory items", labelAr: "حذف أصناف المخزون" },

  // ── Users ────────────────────────────────────────────────────────────────
  { key: "users.view", module: "users", action: "view", labelEn: "View users", labelAr: "عرض المستخدمين" },
  { key: "users.create", module: "users", action: "create", labelEn: "Create users", labelAr: "إضافة المستخدمين" },
  { key: "users.update", module: "users", action: "update", labelEn: "Update users", labelAr: "تعديل المستخدمين" },
  { key: "users.disable", module: "users", action: "disable", labelEn: "Disable users", labelAr: "تعطيل المستخدمين" },

  // ── Roles & permissions ──────────────────────────────────────────────────
  { key: "roles.view", module: "roles", action: "view", labelEn: "View roles", labelAr: "عرض الأدوار" },
  { key: "roles.create", module: "roles", action: "create", labelEn: "Create roles", labelAr: "إضافة الأدوار", sensitive: true },
  { key: "roles.update", module: "roles", action: "update", labelEn: "Update roles and permissions", labelAr: "تعديل الأدوار والصلاحيات", sensitive: true },
  { key: "roles.delete", module: "roles", action: "delete", labelEn: "Delete roles", labelAr: "حذف الأدوار", sensitive: true },

  // ── Settings ─────────────────────────────────────────────────────────────
  { key: "settings.view", module: "settings", action: "view", labelEn: "View settings", labelAr: "عرض الإعدادات" },
  { key: "settings.update", module: "settings", action: "update", labelEn: "Update settings", labelAr: "تعديل الإعدادات", sensitive: true },
  // A complete data export leaves the building in the most literal sense —
  // sensitive for the same reason invoices.download is not automatically
  // granted everywhere: this is every customer, order and invoice at once.
  { key: "settings.export", module: "settings", action: "export", labelEn: "Export all business data", labelAr: "تصدير جميع بيانات المنشأة", sensitive: true },

  // ── Branding ─────────────────────────────────────────────────────────────
  { key: "branding.view", module: "branding", action: "view", labelEn: "View branding", labelAr: "عرض الهوية البصرية" },
  { key: "branding.update", module: "branding", action: "update", labelEn: "Update branding", labelAr: "تعديل الهوية البصرية", sensitive: true },

  // ── Tax & invoice settings ───────────────────────────────────────────────
  { key: "tax_settings.view", module: "tax_settings", action: "view", labelEn: "View tax settings", labelAr: "عرض إعدادات الضريبة" },
  { key: "tax_settings.update", module: "tax_settings", action: "update", labelEn: "Update tax settings", labelAr: "تعديل إعدادات الضريبة", sensitive: true },

  // ── Audit ────────────────────────────────────────────────────────────────
  { key: "audit_logs.view", module: "audit_logs", action: "view", labelEn: "View audit logs", labelAr: "عرض سجل التدقيق" },
] as const satisfies readonly PermissionDefinition[];

/** Union of every valid permission key. Passing anything else is a type error. */
export type PermissionKey = (typeof PERMISSIONS)[number]["key"];

/**
 * The same catalog, widened to `PermissionDefinition[]`.
 *
 * `PERMISSIONS` is a tuple of literal object types, which is what produces the
 * precise `PermissionKey` union — but it also means `sensitive` exists only on
 * the members that declare it, so `p.sensitive` is a type error when iterating.
 * Consumers that read the definitions rather than the keys use this view.
 */
export const PERMISSION_LIST: readonly PermissionDefinition[] = PERMISSIONS;

/** Every permission key, in catalog order. */
export const PERMISSION_KEYS: readonly PermissionKey[] = PERMISSIONS.map((p) => p.key);

const PERMISSION_KEY_SET: ReadonlySet<string> = new Set<string>(PERMISSION_KEYS);

/** Runtime guard for values arriving from the network or the database. */
export function isPermissionKey(value: unknown): value is PermissionKey {
  return typeof value === "string" && PERMISSION_KEY_SET.has(value);
}

export function getPermission(key: PermissionKey): PermissionDefinition {
  const found = PERMISSIONS.find((p) => p.key === key);
  if (!found) {
    // Unreachable while `key` is a PermissionKey, but a database row or an API
    // payload can carry a stale string after a catalog change.
    throw new Error(`Unknown permission key: ${key}`);
  }
  return found;
}

/* ══════════════════════════════════════════════════════════════════════════
   Navigation modules
   ══════════════════════════════════════════════════════════════════════════ */

export interface NavModule {
  readonly key: string;
  readonly route: string;
  readonly labelEn: string;
  readonly labelAr: string;
  /** The module appears in navigation only if the user holds this permission. */
  readonly viewPermission: PermissionKey;
  /** Visual grouping in the sidebar. */
  readonly group: "operations" | "finance" | "insights" | "administration";
}

export const NAV_MODULES = [
  // ORDER IS OPERATIONAL, NOT ALPHABETICAL OR BY GROUP.
  //
  // The web app takes the FIRST FOUR entries a user can see as the mobile
  // bottom-tab bar (MOBILE_TAB_LIMIT in AppShell), and everything after that
  // goes behind "More". So this order decides what a counter operator can
  // reach with one thumb.
  //
  // Invoices sits third, ahead of the catalogue screens: it is a daily
  // counter destination — look up a receipt, reprint, take payment — whereas
  // Services and Pricing are owner-configuration that is set up once and
  // rarely revisited. Measured on the deployed build, the old order put
  // Services in the fourth tab and pushed Invoices into the More sheet,
  // where it was effectively invisible on a phone.
  { key: "dashboard", route: "/dashboard", labelEn: "Dashboard", labelAr: "لوحة التحكم", viewPermission: "dashboard.view", group: "operations" },
  { key: "orders", route: "/orders", labelEn: "Orders", labelAr: "الطلبات", viewPermission: "orders.view", group: "operations" },
  { key: "invoices", route: "/invoices", labelEn: "Invoices", labelAr: "الفواتير", viewPermission: "invoices.view", group: "finance" },
  { key: "customers", route: "/customers", labelEn: "Customers", labelAr: "العملاء", viewPermission: "customers.view", group: "operations" },
  { key: "services", route: "/services", labelEn: "Services", labelAr: "الخدمات", viewPermission: "services.view", group: "operations" },
  { key: "pricing", route: "/pricing", labelEn: "Pricing", labelAr: "الأسعار", viewPermission: "pricing.view", group: "operations" },

  { key: "payments", route: "/payments", labelEn: "Payments", labelAr: "المدفوعات", viewPermission: "payments.view", group: "finance" },
  { key: "expenses", route: "/expenses", labelEn: "Expenses", labelAr: "المصروفات", viewPermission: "expenses.view", group: "finance" },
  { key: "inventory", route: "/inventory", labelEn: "Inventory", labelAr: "المخزون", viewPermission: "inventory.view", group: "finance" },

  { key: "reports", route: "/reports", labelEn: "Reports", labelAr: "التقارير", viewPermission: "reports.view", group: "insights" },

  { key: "users", route: "/users", labelEn: "Users", labelAr: "المستخدمون", viewPermission: "users.view", group: "administration" },
  { key: "roles", route: "/roles", labelEn: "Roles & Permissions", labelAr: "الأدوار والصلاحيات", viewPermission: "roles.view", group: "administration" },
  { key: "settings", route: "/settings", labelEn: "Settings", labelAr: "الإعدادات", viewPermission: "settings.view", group: "administration" },
  { key: "audit_logs", route: "/audit-logs", labelEn: "Audit Logs", labelAr: "سجل التدقيق", viewPermission: "audit_logs.view", group: "administration" },
] as const satisfies readonly NavModule[];

/**
 * Filters navigation for a permission set. UX only — see the file header.
 */
export function visibleNavModules(granted: ReadonlySet<string>): readonly NavModule[] {
  return NAV_MODULES.filter((m) => granted.has(m.viewPermission));
}

/* ══════════════════════════════════════════════════════════════════════════
   System roles seeded on first install
   ══════════════════════════════════════════════════════════════════════════ */

export type SystemRoleKey = "owner" | "super_admin" | "staff";

export interface SystemRoleDefinition {
  readonly key: SystemRoleKey;
  readonly nameEn: string;
  readonly nameAr: string;
  readonly descriptionEn: string;
  readonly descriptionAr: string;
  /**
   * System roles cannot be deleted, and their key cannot change. Their
   * permission sets are still editable by the owner — except the owner role
   * itself, which is immutable (see `isOwnerRole`).
   */
  readonly isSystem: true;
  /**
   * Exactly one role carries this. It is granted every permission in the
   * catalog, is never assignable through ordinary user-management endpoints,
   * and its permission set cannot be edited.
   */
  readonly isOwnerRole: boolean;
}

export const SYSTEM_ROLES = [
  {
    key: "owner",
    nameEn: "Owner",
    nameAr: "المالك",
    descriptionEn: "The highest business authority. Holds every permission and cannot be deleted, demoted, or assigned through ordinary user management.",
    descriptionAr: "أعلى صلاحية في المنشأة. يمتلك جميع الصلاحيات ولا يمكن حذفه أو تخفيض صلاحياته أو إسناده عبر إدارة المستخدمين العادية.",
    isSystem: true,
    isOwnerRole: true,
  },
  {
    key: "super_admin",
    nameEn: "Super Admin",
    nameAr: "مدير النظام",
    descriptionEn: "Runs day-to-day operations and staff. Deliberately excludes business identity, tax configuration, authorization changes, and destruction of financial records.",
    descriptionAr: "يدير العمليات اليومية والموظفين. لا يشمل تعديل هوية المنشأة أو إعدادات الضريبة أو الصلاحيات أو إتلاف السجلات المالية.",
    isSystem: true,
    isOwnerRole: false,
  },
  {
    key: "staff",
    nameEn: "Staff",
    nameAr: "موظف",
    descriptionEn: "Counter and processing staff. Handles orders, customers and payments without administrative or financial-control access.",
    descriptionAr: "موظفو الاستقبال والتشغيل. يتعاملون مع الطلبات والعملاء والمدفوعات دون صلاحيات إدارية أو مالية حساسة.",
    isSystem: true,
    isOwnerRole: false,
  },
] as const satisfies readonly SystemRoleDefinition[];

/**
 * Super Admin's default grants.
 *
 * Written as an explicit list rather than "everything except X" so that adding
 * a new permission to the catalog does NOT silently widen this role. A new
 * permission is granted to Super Admin only by a deliberate edit here.
 *
 * Excluded on purpose: `pricing.update`, `pricing.delete`, `invoices.update`,
 * `invoices.void`, `invoices.delete`, `payments.refund`, `payments.update`,
 * `roles.create`, `roles.update`, `roles.delete`, `settings.update`,
 * `branding.update`, `tax_settings.update` — business identity, tax
 * configuration, authorization itself, and anything that rewrites or destroys
 * financial history stay with the owner.
 *
 * `invoices.issue` IS granted despite being sensitive: issuing moves history
 * forward rather than rewriting it, and a shop whose manager cannot hand a
 * customer a tax invoice does not function.
 */
const SUPER_ADMIN_GRANTS = [
  "dashboard.view",
  "orders.view", "orders.create", "orders.update", "orders.cancel",
  "customers.view", "customers.create", "customers.update", "customers.delete",
  "services.view", "services.create", "services.update", "services.delete",
  "pricing.view",
  "invoices.view", "invoices.create", "invoices.issue", "invoices.print",
  "invoices.export_pdf", "invoices.download",
  "payments.view", "payments.create",
  "expenses.view", "expenses.create", "expenses.update", "expenses.delete",
  "reports.view", "reports.export",
  "inventory.view", "inventory.create", "inventory.update", "inventory.delete",
  "users.view", "users.create", "users.update", "users.disable",
  "roles.view",
  "settings.view",
  "branding.view",
  "tax_settings.view",
  "audit_logs.view",
] as const satisfies readonly PermissionKey[];

/**
 * Staff's default grants: the operational minimum for taking in laundry,
 * serving customers at the counter, and collecting payment.
 *
 * `invoices.issue` is included because handing over a printed tax invoice at
 * the counter IS the counter job. `invoices.download` and `invoices.export_pdf`
 * are not: printing serves the customer in front of you, whereas a downloaded
 * file is a copy of the business's financial records leaving with whoever has
 * the laptop.
 */
const STAFF_GRANTS = [
  "dashboard.view",
  "orders.view", "orders.create", "orders.update",
  "customers.view", "customers.create", "customers.update",
  "services.view",
  "pricing.view",
  "invoices.view", "invoices.create", "invoices.issue", "invoices.print",
  "payments.view", "payments.create",
] as const satisfies readonly PermissionKey[];

/**
 * The permission set each system role is seeded with.
 *
 * The owner role receives the entire catalog as real `role_permissions` rows —
 * not a wildcard and not a bypass in the authorization code — so that every
 * request the owner makes passes through exactly the same permission check as
 * every other user.
 *
 * Consequence to respect in later migrations: any migration that adds a
 * permission MUST also insert the matching `role_permissions` row for the owner
 * role. `scripts/generate-rbac-seed.mjs` emits that automatically for the
 * initial seed, and `verify-phase1` asserts the owner holds the full catalog.
 */
export const DEFAULT_ROLE_GRANTS: Readonly<Record<SystemRoleKey, readonly PermissionKey[]>> = {
  owner: PERMISSION_KEYS,
  super_admin: SUPER_ADMIN_GRANTS,
  staff: STAFF_GRANTS,
};

export const OWNER_ROLE_KEY: SystemRoleKey = "owner";
